import { useState, useEffect } from "react";
import { Mail, Save, RotateCcw, Eye, Loader2, Clock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full bg-[#111] border border-white/10 text-white p-2.5 text-sm focus:border-[#ff0033] outline-none transition-colors placeholder:text-neutral-600";
const btnPrimary = "flex items-center gap-2 bg-[#ff0033] text-white font-mono-lab text-xs uppercase tracking-widest px-4 py-2.5 hover:bg-[#cc0029] transition-colors disabled:opacity-40";
const btnGhost = "flex items-center gap-2 border border-white/15 text-neutral-300 font-mono-lab text-xs uppercase tracking-widest px-4 py-2.5 hover:border-[#ff0033] hover:text-white transition-colors disabled:opacity-40";

const TIMING_FIELDS = [
  ["reminder_days", "Erinnerung nach (Tagen)"],
  ["dunning_days", "Mahnung nach (Tagen)"],
  ["release_days", "Platz automatisch freigeben nach (Tagen)"],
  ["feedback_hours", "Feedback nach Kursende (Stunden)"],
];

const TimingSettings = ({ canManage }) => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/email-workflow").then((r) => setCfg(r.data)).catch((e) => toast.error(apiError(e))); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/email-workflow", cfg);
      setCfg(data);
      toast.success("Zeitintervalle gespeichert");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!cfg) return <div className="text-neutral-500 font-mono-lab text-xs">Lädt…</div>;
  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-5 mb-8" data-testid="email-timing-settings">
      <div className="flex items-center gap-2 mb-4">
        <Clock size={18} className="text-[#ff0033]" />
        <h3 className="font-heading text-lg uppercase tracking-tight text-white">Zeitintervalle</h3>
      </div>
      <p className="text-neutral-400 text-xs mb-5 max-w-2xl">Steuert, wann die automatischen E-Mails versendet werden und wann ein unbezahlter Platz automatisch wieder freigegeben wird.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {TIMING_FIELDS.map(([key, label]) => (
          <label key={key} className="block">
            <span className="font-mono-lab text-[11px] tracking-widest text-neutral-400 uppercase block mb-1.5">{label}</span>
            <input type="number" min="0" value={cfg[key] ?? ""} disabled={!canManage}
              onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })}
              data-testid={`timing-${key}`} className={inputCls} />
          </label>
        ))}
        <label className="block md:col-span-2">
          <span className="font-mono-lab text-[11px] tracking-widest text-neutral-400 uppercase block mb-1.5">Feedback-Link (leer = Kontaktseite)</span>
          <input type="text" value={cfg.feedback_url ?? ""} disabled={!canManage}
            onChange={(e) => setCfg({ ...cfg, feedback_url: e.target.value })}
            placeholder="https://…" data-testid="timing-feedback_url" className={inputCls} />
        </label>
      </div>
      {canManage && (
        <div className="mt-5">
          <button onClick={save} disabled={saving} data-testid="timing-save" className={btnPrimary}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern
          </button>
        </div>
      )}
    </div>
  );
};

const TemplateCard = ({ tpl, canManage, onChanged }) => {
  const [subject, setSubject] = useState(tpl.subject);
  const [body, setBody] = useState(tpl.body);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => { setSubject(tpl.subject); setBody(tpl.body); }, [tpl.key]); // eslint-disable-line

  const dirty = subject !== tpl.subject || body !== tpl.body;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/email-templates/${tpl.key}`, { subject, body });
      toast.success(`„${tpl.label}" gespeichert`);
      onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm(`„${tpl.label}" auf den Standardtext zurücksetzen?`)) return;
    setSaving(true);
    try {
      await api.delete(`/admin/email-templates/${tpl.key}`);
      toast.success(`„${tpl.label}" zurückgesetzt`);
      onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const doPreview = async () => {
    try {
      const { data } = await api.post(`/admin/email-templates/${tpl.key}/preview`, { subject, body });
      setPreview(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-5" data-testid={`email-tpl-${tpl.key}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-heading text-lg uppercase tracking-tight text-white">{tpl.label}</h3>
        {tpl.customized
          ? <span className="font-mono-lab text-[10px] tracking-widest uppercase text-[#ff0033] border border-[#ff0033]/40 px-2 py-1" data-testid={`email-tpl-${tpl.key}-badge`}>Angepasst</span>
          : <span className="font-mono-lab text-[10px] tracking-widest uppercase text-neutral-500 border border-white/10 px-2 py-1">Standard</span>}
      </div>

      <div className="mb-3">
        <span className="font-mono-lab text-[10px] tracking-widest text-neutral-500 uppercase">Platzhalter (klicken zum Kopieren)</span>
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {tpl.placeholders.map((p) => (
            <button key={p} type="button" onClick={() => { navigator.clipboard?.writeText(`{${p}}`); toast.message(`{${p}} kopiert`); }}
              className="font-mono-lab text-[11px] text-neutral-300 bg-[#111] border border-white/10 px-2 py-1 hover:border-[#ff0033] hover:text-white transition-colors">{`{${p}}`}</button>
          ))}
        </div>
      </div>

      <label className="block mb-3">
        <span className="font-mono-lab text-[11px] tracking-widest text-neutral-400 uppercase block mb-1.5">Betreff</span>
        <input value={subject} disabled={!canManage} onChange={(e) => setSubject(e.target.value)}
          data-testid={`email-tpl-${tpl.key}-subject`} className={inputCls} />
      </label>

      <label className="block">
        <span className="font-mono-lab text-[11px] tracking-widest text-neutral-400 uppercase block mb-1.5">Text</span>
        <textarea value={body} disabled={!canManage} onChange={(e) => setBody(e.target.value)} rows={12}
          data-testid={`email-tpl-${tpl.key}-body`} className={`${inputCls} font-mono-lab leading-relaxed resize-y`} />
      </label>

      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={doPreview} data-testid={`email-tpl-${tpl.key}-preview`} className={btnGhost}><Eye size={14} /> Vorschau</button>
        {canManage && <button onClick={save} disabled={saving || !dirty} data-testid={`email-tpl-${tpl.key}-save`} className={btnPrimary}>{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern</button>}
        {canManage && tpl.customized && <button onClick={reset} disabled={saving} data-testid={`email-tpl-${tpl.key}-reset`} className={btnGhost}><RotateCcw size={14} /> Auf Standard</button>}
      </div>

      {preview && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" data-testid={`email-tpl-${tpl.key}-preview-modal`}>
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setPreview(null)} />
          <div className="relative bg-[#0A0A0A] border border-white/10 w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6">
            <span className="font-mono-lab text-[10px] tracking-widest text-neutral-500 uppercase">Vorschau (mit Beispieldaten)</span>
            <h4 className="font-heading text-xl text-white mt-1 mb-4">{preview.subject}</h4>
            <pre className="text-neutral-200 text-sm whitespace-pre-wrap font-body leading-relaxed">{preview.body}</pre>
            <button onClick={() => setPreview(null)} className={`${btnPrimary} mt-6`}>Schließen</button>
          </div>
        </div>
      )}
    </div>
  );
};

const EmailTemplates = ({ canManage }) => {
  const [templates, setTemplates] = useState(null);

  const load = () => api.get("/admin/email-templates").then((r) => setTemplates(r.data.templates)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="email-templates-page">
      <div className="flex items-center gap-2 mb-2">
        <Mail size={22} className="text-[#ff0033]" />
        <h2 className="font-heading text-2xl uppercase tracking-tight text-white">E-Mail-Texte</h2>
      </div>
      <p className="text-neutral-400 text-sm mb-6 max-w-2xl">Betreff und Text der automatischen Zahlungs- und Feedback-E-Mails frei bearbeiten. Verwende die Platzhalter (z. B. <span className="font-mono-lab text-neutral-200">{"{name}"}</span>), sie werden beim Versand automatisch eingesetzt.</p>

      <TimingSettings canManage={canManage} />

      {!templates ? (
        <div className="text-neutral-500 font-mono-lab text-xs">Lädt…</div>
      ) : (
        <div className="space-y-6">
          {templates.map((tpl) => <TemplateCard key={tpl.key} tpl={tpl} canManage={canManage} onChanged={load} />)}
        </div>
      )}
    </div>
  );
};

export default EmailTemplates;
